import { createElement } from 'react';
import { renderToString } from 'react-dom/server';
import { MapLocation } from '@/types/location';

// Import 3D icon components
import { Billboard3DIcon } from '@/components/icons/3d/Billboard3DIcon';
import { Barbershop3DIcon } from '@/components/icons/3d/Barbershop3DIcon';
import { School3DIcon } from '@/components/icons/3d/School3DIcon';
import { Cafe3DIcon } from '@/components/icons/3d/Cafe3DIcon';
import { Restaurant3DIcon } from '@/components/icons/3d/Restaurant3DIcon';
import { Lounge3DIcon } from '@/components/icons/3d/Lounge3DIcon';
import { Gym3DIcon } from '@/components/icons/3d/Gym3DIcon';
import { GameCenter3DIcon } from '@/components/icons/3d/GameCenter3DIcon';
import { Gate3DIcon } from '@/components/icons/3d/Gate3DIcon';
import { Unipole3DIcon } from '@/components/icons/3d/Unipole3DIcon';
import { Default3DIcon } from '@/components/icons/3d/Default3DIcon';
import { BridgeScreen3DIcon } from '@/components/icons/3d/BridgeScreen3DIcon';
import { MegacomScreen3DIcon } from '@/components/icons/3d/MegacomScreen3DIcon';
import { SuperMegaScreen3DIcon } from '@/components/icons/3d/SuperMegaScreen3DIcon';
import { BuildingScreen3DIcon } from '@/components/icons/3d/BuildingScreen3DIcon';

interface IconDef {
  component: React.ComponentType<{ size?: number; color?: string }>;
  color: string;
}

// All unique icon+color combos to register
const ICON_REGISTRY: Record<string, IconDef> = {
  'billboard-pink': { component: Billboard3DIcon, color: '#BC1A6C' },
  'billboard-green': { component: Billboard3DIcon, color: '#0b7b41' },
  'bridge-pink': { component: BridgeScreen3DIcon, color: '#BC1A6C' },
  'megacom-pink': { component: MegacomScreen3DIcon, color: '#BC1A6C' },
  'supermega-pink': { component: SuperMegaScreen3DIcon, color: '#BC1A6C' },
  'buildingscreen-pink': { component: BuildingScreen3DIcon, color: '#BC1A6C' },
  'school-green': { component: School3DIcon, color: '#10B981' },
  'cafe-brown': { component: Cafe3DIcon, color: '#8B4513' },
  'restaurant-red': { component: Restaurant3DIcon, color: '#FF4500' },
  'lounge-blue': { component: Lounge3DIcon, color: '#2478C8' },
  'lounge-purple': { component: Lounge3DIcon, color: '#7E69AB' },
  'gym-purple': { component: Gym3DIcon, color: '#8B5CF6' },
  'game-amber': { component: GameCenter3DIcon, color: '#F59E0B' },
  'gate-green': { component: Gate3DIcon, color: '#0b7b41' },
  'unipole-green': { component: Unipole3DIcon, color: '#0b7b41' },
  'default-indigo': { component: Default3DIcon, color: '#6366f1' },
  'default-purple': { component: Default3DIcon, color: '#8B5CF6' },
  'default-green': { component: Default3DIcon, color: '#0b7b41' },
  'barbershop-pink': { component: Barbershop3DIcon, color: '#E91E63' },
};

const ICON_SIZE = 96; // render size for crisp sprites

function svgToImage(svgString: string, size: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.width = size;
    img.height = size;
    img.src = url;
  });
}

export async function loadMapboxIcons(map: mapboxgl.Map): Promise<void> {
  const entries = Object.entries(ICON_REGISTRY);

  await Promise.all(
    entries.map(async ([name, { component, color }]) => {
      if (map.hasImage(name)) return;

      const svgString = renderToString(
        createElement(component, { size: ICON_SIZE, color })
      );

      const img = await svgToImage(svgString, ICON_SIZE);

      const canvas = document.createElement('canvas');
      canvas.width = ICON_SIZE;
      canvas.height = ICON_SIZE;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, ICON_SIZE, ICON_SIZE);

      if (!map.hasImage(name)) {
        map.addImage(name, ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE), {
          pixelRatio: 2,
        });
      }
    })
  );
}

// Map from custom_icon keys to their components for dynamic registration
const CUSTOM_ICON_COMPONENTS: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  billboard: Billboard3DIcon,
  school: School3DIcon,
  cafe: Cafe3DIcon,
  restaurant: Restaurant3DIcon,
  lounge: Lounge3DIcon,
  gym: Gym3DIcon,
  gamecenter: GameCenter3DIcon,
  gate: Gate3DIcon,
  unipole: Unipole3DIcon,
  bridge: BridgeScreen3DIcon,
  megacom: MegacomScreen3DIcon,
  supermega: SuperMegaScreen3DIcon,
  buildingscreen: BuildingScreen3DIcon,
  barbershop: Barbershop3DIcon,
  default: Default3DIcon,
};

// Register a dynamic icon+color combo on the fly
export async function ensureDynamicIcon(map: mapboxgl.Map, iconKey: string, componentKey: string, color: string): Promise<void> {
  if (map.hasImage(iconKey)) return;
  const Comp = CUSTOM_ICON_COMPONENTS[componentKey];
  if (!Comp) return;

  const svgString = renderToString(createElement(Comp, { size: ICON_SIZE, color }));
  const img = await svgToImage(svgString, ICON_SIZE);
  const canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, ICON_SIZE, ICON_SIZE);

  if (!map.hasImage(iconKey)) {
    map.addImage(iconKey, ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE), { pixelRatio: 2 });
  }
}

export function getIconName(location: MapLocation, customIcon?: string, customColor?: string): string {
  // If a custom icon override exists, use the dynamic key
  if (customIcon && customColor) {
    return `custom-${customIcon}-${customColor.replace('#', '')}`;
  }

  const { mediaOwner, type, segment, name: locationName } = location;

  if (mediaOwner === 'Attention') {
    if (type.toLowerCase().includes('digital super mega')) return 'supermega-pink';
    if (type.toLowerCase().includes('digital megacom')) return 'megacom-pink';
    if (locationName?.toLowerCase().includes('bridge')) return 'bridge-pink';
    if (type.toLowerCase() === 'building screens') return 'buildingscreen-pink';
    if (type.toLowerCase() === 'curved led screen') return 'billboard-pink';
    if (type.toLowerCase() === 'led screen') return 'billboard-pink';
    return 'billboard-pink';
  }

  if (mediaOwner === 'Waki') return 'school-green';

  if (mediaOwner === 'Alan') {
    if (type.toLowerCase() === 'gym') return 'gym-purple';
    if (type.toLowerCase() === 'game center') return 'game-amber';
    if (segment?.toLowerCase() === 'shisha' || segment?.toLowerCase() === 'lounge') return 'lounge-purple';
    if (segment?.toLowerCase() === 'cafe') return 'cafe-brown';
    return 'default-purple';
  }

  if (mediaOwner === 'Rotana Signs') {
    if (type.toLowerCase().includes('digital unipole')) return 'unipole-green';
    if (type.toLowerCase().includes('smart gate')) return 'gate-green';
    if (type.toLowerCase().includes('hyper digital') || type.toLowerCase().includes('digital screen')) return 'billboard-green';
    return 'default-green';
  }

  if (mediaOwner === 'On Spot') return 'barbershop-pink';

  switch (type.toLowerCase()) {
    case 'cafe': return 'cafe-brown';
    case 'restaurant': return 'restaurant-red';
    case 'lounge': return 'lounge-blue';
    case 'gym': return 'gym-purple';
    case 'game center': return 'game-amber';
    case 'school': return 'school-green';
    case 'building screens':
    case 'outdoor billboard':
      return 'buildingscreen-pink';
    case 'digital megacom':
      return 'megacom-pink';
    case 'digital super mega':
      return 'supermega-pink';
    case 'curved led screen':
    case 'led screen':
      return 'billboard-pink';
    default:
      return 'default-indigo';
  }
}
